/**
 * ValorReferenciaFormDialog - Crear/Editar valor de referencia
 */

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useApiError } from "@/hooks/use-api-error";
import { useCreateValorReferencia, useUpdateValorReferencia } from "@/hooks/use-ordenes";
import {
  yearsToDays,
  valorReferenciaFormSchema,
  type ValorReferenciaFormValues,
} from "./valor-referencia-utils";

// =============================================================================
// Component
// =============================================================================

const SEX_OPTIONS = ["M", "F", "A"] as const;

interface ValorReferenciaFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  examenTipoId: number;
  valorReferenciaId: number | null;
  defaultValues?: Partial<ValorReferenciaFormValues>;
  onSuccess: () => void;
}

export function ValorReferenciaFormDialog({
  open,
  onOpenChange,
  examenTipoId,
  valorReferenciaId,
  defaultValues,
  onSuccess,
}: ValorReferenciaFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const isNew = valorReferenciaId === null;

  const form = useForm({
    resolver: zodResolver(valorReferenciaFormSchema),
    defaultValues: {
      sexo: "A" as const,
      edad_min_anios: undefined as number | undefined,
      edad_max_anios: undefined as number | undefined,
      valor_min: undefined as number | undefined | null,
      valor_max: undefined as number | undefined | null,
      valor_texto: "",
      unidad: "",
      descripcion: "",
    },
  });

  // Populate form when editing or reset when creating
  useEffect(() => {
    if (!open) return;

    if (!isNew && defaultValues) {
      form.reset({
        sexo: defaultValues.sexo ?? "A",
        edad_min_anios: defaultValues.edad_min_anios,
        edad_max_anios: defaultValues.edad_max_anios,
        valor_min: defaultValues.valor_min,
        valor_max: defaultValues.valor_max,
        valor_texto: defaultValues.valor_texto ?? "",
        unidad: defaultValues.unidad ?? "",
        descripcion: defaultValues.descripcion ?? "",
      });
    } else if (isNew) {
      form.reset({
        sexo: "A",
        edad_min_anios: undefined,
        edad_max_anios: undefined,
        valor_min: undefined,
        valor_max: undefined,
        valor_texto: "",
        unidad: "",
        descripcion: "",
      });
    }
  }, [open, isNew, defaultValues, form]);

  const createMutation = useCreateValorReferencia({
    onSuccess: () => {
      toast.success(t("ordenes.valoresReferencia.toast.created"));
      onOpenChange(false);
      onSuccess();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const updateMutation = useUpdateValorReferencia({
    onSuccess: () => {
      toast.success(t("ordenes.valoresReferencia.toast.updated"));
      onOpenChange(false);
      onSuccess();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const onSubmit = (values: ValorReferenciaFormValues) => {
    const payload = {
      examen_tipo: examenTipoId,
      sexo: values.sexo,
      edad_min_dias: yearsToDays(values.edad_min_anios),
      edad_max_dias: yearsToDays(values.edad_max_anios),
      valor_min: values.valor_min ?? undefined,
      valor_max: values.valor_max ?? undefined,
      valor_texto: values.valor_texto || undefined,
      unidad: values.unidad || undefined,
      descripcion: values.descripcion || undefined,
    };

    if (isNew) {
      createMutation.mutate(payload);
    } else {
      updateMutation.mutate({ id: valorReferenciaId!, data: payload });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? t("ordenes.valoresReferencia.newRange")
              : t("ordenes.valoresReferencia.editRange")}
          </DialogTitle>
          <DialogDescription>
            {isNew
              ? t("ordenes.valoresReferencia.newRangeDesc")
              : t("ordenes.valoresReferencia.editRangeDesc")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="sexo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("ordenes.valoresReferencia.fields.sex")}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {SEX_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>
                          {t(`ordenes.valoresReferencia.sex.${s}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="edad_min_anios"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.valoresReferencia.fields.ageMin")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        placeholder="0"
                        ref={field.ref}
                        name={field.name}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        value={(field.value as number) ?? ""}
                        onChange={(e) =>
                          field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="edad_max_anios"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.valoresReferencia.fields.ageMax")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        placeholder="120"
                        ref={field.ref}
                        name={field.name}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        value={(field.value as number) ?? ""}
                        onChange={(e) =>
                          field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="valor_min"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.valoresReferencia.fields.valueMin")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="any"
                        ref={field.ref}
                        name={field.name}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        value={(field.value as number) ?? ""}
                        onChange={(e) =>
                          field.onChange(e.target.value === "" ? null : Number(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="valor_max"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.valoresReferencia.fields.valueMax")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="any"
                        ref={field.ref}
                        name={field.name}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        value={(field.value as number) ?? ""}
                        onChange={(e) =>
                          field.onChange(e.target.value === "" ? null : Number(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="valor_texto"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("ordenes.valoresReferencia.fields.valueText")}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="unidad"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("ordenes.valoresReferencia.fields.unit")}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="mg/dL" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="descripcion"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("ordenes.valoresReferencia.fields.description")}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? t("common.saving") : t("common.save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
